import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { LayersIcon, ZapIcon, UsersIcon, BriefcaseIcon, VideoIcon, MessageSquareIcon } from 'lucide-react';
const features = [{
  icon: LayersIcon,
  title: 'Project-Based Learning',
  description: 'Build 8+ real-world projects that you can add directly to your portfolio. No theoretical fluff.'
}, {
  icon: ZapIcon,
  title: 'AI-Powered Design',
  description: 'Learn to leverage AI tools like Midjourney, ChatGPT, and Figma AI to 10x your design workflow.'
}, {
  icon: UsersIcon,
  title: 'Live Cohort Experience',
  description: 'Learn alongside 50 motivated designers with weekly live sessions, critiques, and networking.'
}, {
  icon: BriefcaseIcon,
  title: 'Career Support',
  description: 'Resume reviews, portfolio feedback, and direct introductions to hiring partners at top companies.'
}, {
  icon: VideoIcon,
  title: 'Lifetime Access',
  description: 'All course materials, future updates, and community access forever. Learn at your own pace.'
}, {
  icon: MessageSquareIcon,
  title: '1-on-1 Mentorship',
  description: 'Weekly office hours with senior designers from Google, Meta, and other top companies.'
}];
export function FeaturesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 bg-dark-800 relative overflow-hidden" data-id="element-120">
      {/* Background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-brand-yellow/30 to-transparent" data-id="element-121" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-122">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-123">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-124">
            Why LeaDe Academy
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-125">
            Everything you need to
            <br data-id="element-126" />
            <span className="text-gradient-yellow" data-id="element-127">succeed in design</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-128">
            We've designed every aspect of this program to maximize your
            learning and career outcomes. No fluff, just results.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-129">
          {features.map((feature, index) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1 * index
        }} className="group relative p-8 rounded-2xl bg-dark-700/50 border border-white/5 hover:border-brand-yellow/20 transition-all duration-300" data-id="element-130">
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-brand-yellow/10 flex items-center justify-center mb-6 group-hover:bg-brand-yellow/20 transition-colors" data-id="element-131">
                <feature.icon className="w-6 h-6 text-brand-yellow" data-id="element-132" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-white mb-3" data-id="element-133">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-134">
                {feature.description}
              </p>

              {/* Hover glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-brand-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-135" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}